'use client';

import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  type ReactNode,
  type CSSProperties,
} from 'react';

import { useResponsive } from '@depop/web-ui-kit/useResponsive';

import styles from './styles.module.css';

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type ItemsPerSlide = Record<Size, number>;

export type NavigationState = {
  canGoLeft: boolean;
  canGoRight: boolean;
  goLeft: () => void;
  goRight: () => void;
};

export type Props = {
  items: ReactNode[];
  itemsPerSlide?: ItemsPerSlide;
  itemGap?: number;
  id?: string;
  ariaLabel?: string;
  onSlideChange?: (slide: number) => void;
  onNavigationStateChange?: (state: NavigationState) => void;
} & React.ComponentPropsWithRef<'div'>;

const DEFAULT_ITEMS_PER_SLIDE: ItemsPerSlide = {
  xs: 1,
  sm: 1,
  md: 1,
  lg: 1,
  xl: 1,
};

type CSSVarKeys =
  | '--items-per-slide-xs'
  | '--items-per-slide-sm'
  | '--items-per-slide-md'
  | '--items-per-slide-lg'
  | '--items-per-slide-xl'
  | '--carousel-gap';

type CSSVars = { [K in CSSVarKeys]?: string | number };

export function Carousel({
  id,
  items,
  itemsPerSlide = DEFAULT_ITEMS_PER_SLIDE,
  itemGap,
  ariaLabel = 'Carousel',
  onSlideChange,
  onNavigationStateChange,
  ...rest
}: Props) {
  const { isMaxSmall, isMaxMedium, isMaxLarge, isMaxExtraLarge } =
    useResponsive();

  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const [containerWidth, setContainerWidth] = useState(0);
  const [gapPx, setGapPx] = useState(0);
  const [maxTranslatePx, setMaxTranslatePx] = useState(0);

  function getItemsPerPage() {
    if (isMaxSmall) {
      return itemsPerSlide.xs;
    }
    if (isMaxMedium) {
      return itemsPerSlide.sm;
    }
    if (isMaxLarge) {
      return itemsPerSlide.md;
    }
    if (isMaxExtraLarge) {
      return itemsPerSlide.lg;
    }
    return itemsPerSlide.xl;
  }

  const itemsPerPage = Math.max(1, getItemsPerPage());
  const totalPages = Math.max(1, Math.ceil(items.length / itemsPerPage));
  const canGoLeft = currentIndex > 0;
  const canGoRight = currentIndex < totalPages - 1;

  function goLeft() {
    if (canGoLeft) {
      setCurrentIndex((i) => i - 1);
    }
  }

  function goRight() {
    if (canGoRight) {
      setCurrentIndex((i) => i + 1);
    }
  }

  useEffect(() => {
    setCurrentIndex(0);
  }, [itemsPerPage]);

  useEffect(() => {
    onNavigationStateChange?.({
      canGoLeft,
      canGoRight,
      goLeft,
      goRight,
    });
  }, [canGoLeft, canGoRight, onNavigationStateChange]);

  useEffect(() => {
    onSlideChange?.(currentIndex);
  }, [currentIndex, onSlideChange]);

  useLayoutEffect(() => {
    function updateMetrics() {
      const containerEl = containerRef.current;
      if (!containerEl) {
        return;
      }

      const trackEl = containerEl.querySelector<HTMLElement>(
        `.${styles.carousel}`
      );
      if (!trackEl) {
        return;
      }

      const cw = containerEl.clientWidth;
      setContainerWidth(trackEl.clientWidth);

      const computed = window.getComputedStyle(trackEl);
      const cg = parseFloat(computed.columnGap || '0');
      setGapPx(Number.isFinite(cg) ? cg : 0);

      const diffPx = trackEl.scrollWidth - cw;
      setMaxTranslatePx(Math.max(0, diffPx));
    }

    updateMetrics();
    window.addEventListener('resize', updateMetrics);
    return () => window.removeEventListener('resize', updateMetrics);
  }, [items.length, itemsPerPage]);

  const translatePx = Math.min(
    currentIndex * (containerWidth + gapPx),
    maxTranslatePx
  );

  // Calculate which items are in the active slide
  const startIndex = currentIndex * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, items.length);

  return (
    <div
      className={styles.carouselContainer}
      id={id}
      role="region"
      aria-label={ariaLabel}
      {...rest}
    >
      <div className={styles.itemsContainer} ref={containerRef}>
        <div
          className={styles.carousel}
          role="group"
          aria-label="Carousel items"
          style={(() => {
            const base: CSSProperties & CSSVars = {
              transform: `translateX(-${translatePx}px)`,
              ['--items-per-slide-xs']: itemsPerSlide.xs,
              ['--items-per-slide-sm']: itemsPerSlide.sm,
              ['--items-per-slide-md']: itemsPerSlide.md,
              ['--items-per-slide-lg']: itemsPerSlide.lg,
              ['--items-per-slide-xl']: itemsPerSlide.xl,
            };

            if (typeof itemGap === 'number') {
              base.gap = `${itemGap}px`;
              base['--carousel-gap'] = `${itemGap}px`;
            }

            return base;
          })()}
        >
          {items.map((item, index) => {
            const isActive = index >= startIndex && index < endIndex;

            return (
              <div
                key={index}
                className={styles.carouselItem}
                role="group"
                aria-label={`Item ${index + 1}`}
                inert={!isActive ? true : undefined}
              >
                {item}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
